import { useQuery } from "@tanstack/react-query"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { VENDOR_CREDITS_ROUTES } from "@routes/vendorCredits"

import { VendorCreditStatuses } from "@constants/choices"

import { VendorCreditListResponse } from "./BillApplyVendorCreditDialog.types"

/**
 * Fetch the vendor's recorded credits that still have value left to apply, oldest first.
 *
 * The list is paginated a handful of credits at a time, so every page is followed. The status filter already leaves
 * out drafts, voided credits and consumed ones; the remaining check drops a recorded credit worth nothing.
 */
async function fetchApplicableVendorCredits(endpoint: string, vendorID: string): Promise<VendorCredit[]> {
    const params = new URLSearchParams({
        vendor: vendorID,
        status: String(VendorCreditStatuses.recorded),
        ordering: "date_issued",
    })

    const vendorCredits: VendorCredit[] = []
    let pageURL: string | null = `${endpoint}?${params.toString()}`

    while (pageURL) {
        const response = await fetch(pageURL)

        if (!response.ok) {
            throw new Error("Failed to fetch the vendor's credits")
        }

        const page = (await response.json()) as VendorCreditListResponse
        vendorCredits.push(...page.results)
        pageURL = page.next
    }

    return vendorCredits.filter((vendorCredit) => Number(vendorCredit.amount_remaining) > 0)
}

export function useApplicableVendorCreditsQuery(vendorID: string) {
    const { user } = useUser()
    const [endpoint] = useSlugExtractor([VENDOR_CREDITS_ROUTES.LIGHTWEIGHT_LIST])

    return useQuery({
        queryKey: ["applicableVendorCredits", endpoint, vendorID],
        queryFn: () => fetchApplicableVendorCredits(endpoint, vendorID),
        enabled: !!user?.service_company?.slug,
    })
}
